<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :scrollable="true"
    icon="fa-microchip"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="stationForm" :labelColumnSize="3">
      <FormRow label="Hardware Type">
        <FormSelect :options="ProductionStationHardwareTypeLookup" v-model="stationData.hardwareType" />
      </FormRow>

      <FormHorizontalRule />

      <template v-if="isCuttingOptimisationStation">
        <ContentTitle>Optimiser Hardware Configuration</ContentTitle>
        <FormRow label="Export Origin Point" helpTip="Corner of the sheet that measurement starts from for exporting">
          <FormSelect :options="optimiserExportOriginOptions" :value="optimiserExportOrigin" @input="updateOptimiserExportOrigin" />
        </FormRow>
        <FormRow label="Horizontal Fill Direction" helpTip="Fill direction the optimiser packs items into the sheet along the horizontal axis">
          <FormSelect :options="optimiserFillHorzOptions" v-model="optimiserConfig.fillHorzDirection" />
        </FormRow>
        <FormRow label="Vertical Fill Direction" helpTip="Fill direction the optimiser packs items into the sheet along the vertical axis">
          <FormSelect :options="optimiserFillVertOptions" v-model="optimiserConfig.fillVertDirection" />
        </FormRow>
        <FormRow label="Max Cutting Depth" helpTip="Maximum number of nested cutting planes used for cuts. Based on hardware limits">
          <FormInputText type="number" v-model="optimiserConfig.maxCuttingDepth" />          
        </FormRow>
        
        <FormRow label="Offcut Preference" helpTip="Controls how offcuts are preferred in optimiser sheets">
          <FormSelect :options="OffcutScoreConfigTypeLookup" v-model="optimiserConfig.offcutScoringMethod" />
        </FormRow>
        <FormRow label="Offcut Threshold" helpTip="Threshold percentage for offcut count testing. If the area of offcuts compared between two sheets is greater than the percent, offcut area is used" v-if="optimiserConfig.offcutScoringMethod == OffcutScoreConfigTypes.lesserOffcutCount">
          <FormInputText type="number" v-model="optimiserConfig.offcutScoringPercent" addon="%" />          
        </FormRow>
        <FormRow label="Offcut Overflow Enabled" helpTip="If set, will cause offcuts near the sheet edges to extend past the trim to the sheet edge">
          <FormCheckbox v-model="optimiserConfig.offcutOverflowEnabled" />          
        </FormRow>
        
      </template>

      <template v-if="isWaterjetStation">
        <ContentTitle>Waterjet Hardware Configuration</ContentTitle>
        <FormRow label="Waterjet Positions" helpTip="Positions along x axis on the waterjet bed if multiple panels can be cut">
          <StaticDataTable class="static-table-list">
            <template v-slot:header>
              <tr>
                <th width="95%">Position</th>
                <th width="5%">Action</th>
              </tr>
            </template>
            <template v-slot:body>
              <tr v-for="(position, index) of bedConfig.positionList" :key="index">
                <td>
                  <FormInputText type="number" :value="position" @input="updateWaterjetPosition(index, $event)" addon="mm" />
                </td>
                <td>
                  <button class="btn btn-white btn-xs" @click.prevent="removeWaterjetPosition(index)"><i class="far fa-trash-alt"></i></button>
                </td>
              </tr>
            </template>
          </StaticDataTable>
          <button class="btn btn-white btn-xs" @click.prevent="addWaterjetPosition"><i class="far fa-plus"></i> Add Position</button>
        </FormRow>

        <FormRow label="Waterjet Bed Height">
          <FormInputText type="number" v-model="bedConfig.height" addon="mm" />
        </FormRow>
        <FormRow label="Waterjet Bed Width">
          <FormInputText type="number" v-model="bedConfig.width" addon="mm" />
        </FormRow>
        <FormRow label="Waterjet Home Position" helpTip="Default position where the jet head is placed before cutting" :fieldCount="2">
          <template v-slot:field1>
            <FormInputText type="number" v-model="bedConfig.homePositionX" addon="mm" prepend="X" />
          </template>
          <template v-slot:field2>
            <FormInputText type="number" v-model="bedConfig.homePositionY" addon="mm" prepend="Y" />
          </template>
        </FormRow>
        <FormRow label="Waterjet Bed Origin Point" helpTip="Corner of the waterjet bed that measurement starts from relative to the operator">
          <FormSelect :options="waterjetOriginOptions" :value="waterjetOrigin" @input="updateWaterjetOrigin" />
        </FormRow>
        <FormRow label="Waterjet Origin Guide Height" helpTip="The height of the guide at the origin that panels rest against. Set to zero to ignore">
          <FormInputText type="number" v-model="bedConfig.originGuideHeight" addon="mm" />
        </FormRow>
        <FormRow label="Waterjet Origin Guide Width" helpTip="The width of the guide at the origin that panels rest against. Set to zero to ignore">
          <FormInputText type="number" v-model="bedConfig.originGuideWidth" addon="mm" />
        </FormRow>
        <FormRow label="Waterjet Max Negative Height" helpTip="The maximum distance on the negative Y axis beyond the origin point">
          <FormInputText type="number" v-model="bedConfig.maxNegativeHeight" addon="mm" />
        </FormRow>
        <FormRow label="Waterjet Max Negative Width" helpTip="The maximum distance on the negative X axis beyond the origin point">
          <FormInputText type="number" v-model="bedConfig.maxNegativeWidth" addon="mm" />
        </FormRow>
      </template>

      <template v-if="isFeedInStation">
        <ContentTitle>Feed In Configuration</ContentTitle>
        <FormRow label="Max Feed Height" helpTip="Maximum height supported of the panel when placed onto the feed in conveyor">
          <FormInputText type="number" v-model="stationData.maxFeedHeight" addon="mm" />
        </FormRow>
      </template>

      <template v-if="isFurnaceStation">
        <FormRow label="Furnace Bed Height" helpTip="Height of the load in bed">
          <FormInputText type="number" v-model="furnaceConfig.bedHeight" addon="mm" />
        </FormRow>
        <FormRow label="Furnace Bed Width" helpTip="Width of the load in bed">
          <FormInputText type="number" v-model="furnaceConfig.bedWidth" addon="mm" />
        </FormRow>
      </template>

      
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import { ProductionStationTypes, ProductionStationTypeLookup, ProductionOptimisationCutTypes, ProductionOptimisationCutTypeLookup, ProductionStationHardwareTypes, ProductionStationHardwareTypeLookup } from '@/store/types'
import { OptimiserFillDirectionTypes, OptimiserFillDirectionTypeLookup, OffcutScoreConfigTypes, OffcutScoreConfigTypeLookup } from '@/store/types'
import { AlignmentTypes } from '@/store/mathTypes'

export default {
  name: 'ProductionStationHardwareDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog
  },
  props: {
    productionStationId: Number
  },
  data() {
    return {
      stationData: {
        hardwareType: ProductionStationHardwareTypes.generic,
        maxFeedHeight: 0
      },
      bedConfig: {
        positionList: [],
        width: 0,
        height: 0,
        homePositionX: 0,
        homePositionY: 0,
        originHorizontal: AlignmentTypes.left,
        originVertical: AlignmentTypes.bottom,
        originGuideHeight: 0,
        originGuideWidth: 0,
        maxNegativeHeight: 0,
        maxNegativeWidth: 0
      },
      optimiserConfig: {
        exportOriginHorizontal: AlignmentTypes.left,
        exportOriginVertical: AlignmentTypes.bottom,
        fillHorzDirection: OptimiserFillDirectionTypes.right,
        fillVertDirection: OptimiserFillDirectionTypes.up,
        offcutScoringMethod: OffcutScoreConfigTypes.greaterOffcutArea,
        offcutScoringPercent: 50,
        maxCuttingDepth: 9,
        offcutOverflowEnabled: false
      },
      furnaceConfig: {
        bedWidth: 0,
        bedHeight: 0
      },
      stationForm: {},
      submitting: false
    }
  },
  created() {
    this.ProductionStationTypes = ProductionStationTypes
    this.ProductionStationTypeLookup = ProductionStationTypeLookup
    this.ProductionOptimisationCutTypes = ProductionOptimisationCutTypes
    this.ProductionOptimisationCutTypeLookup = ProductionOptimisationCutTypeLookup
    this.ProductionStationHardwareTypeLookup = ProductionStationHardwareTypeLookup
    this.OffcutScoreConfigTypes = OffcutScoreConfigTypes
    this.OffcutScoreConfigTypeLookup = OffcutScoreConfigTypeLookup
  },
  mounted() {
    
  },
  computed: {
    title() {
      return 'Edit Production Station Hardware'
    },
    submitTitle() {
      return 'Update'
    },
     isCuttingStation() {
      return this.$data.stationData.type == ProductionStationTypes.cutting
    },
    isCuttingOptimisationStation() {
      return this.$data.stationData.type == ProductionStationTypes.cuttingOptimisation
    },
    isWaterjetStation() {
      return this.$data.stationData.type == ProductionStationTypes.waterjet
    },
    isFurnaceStation() {
      return this.$data.stationData.type == ProductionStationTypes.furnace
    },
    isFeedInStation() {
      return this.$data.stationData.type == ProductionStationTypes.feedIn
    },
    waterjetOriginOptions() {
      return [
        { name: 'Bottom Left', value: `${AlignmentTypes.bottom},${AlignmentTypes.left}`, originHorizontal: AlignmentTypes.left, originVertical: AlignmentTypes.bottom },
        { name: 'Bottom Right', value: `${AlignmentTypes.bottom},${AlignmentTypes.right}`, originHorizontal: AlignmentTypes.right, originVertical: AlignmentTypes.bottom },
        { name: 'Top Left', value: `${AlignmentTypes.top},${AlignmentTypes.left}`, originHorizontal: AlignmentTypes.left, originVertical: AlignmentTypes.top },
        { name: 'Top Right', value: `${AlignmentTypes.top},${AlignmentTypes.right}`, originHorizontal: AlignmentTypes.right, originVertical: AlignmentTypes.top }
      ]
    },
    waterjetOrigin() {
      return `${this.bedConfig.originVertical},${this.bedConfig.originHorizontal}`
    },
    optimiserExportOriginOptions() {
      return [
        { name: 'Bottom Left', value: `${AlignmentTypes.bottom},${AlignmentTypes.left}`, exportOriginHorizontal: AlignmentTypes.left, exportOriginVertical: AlignmentTypes.bottom },
        { name: 'Bottom Right', value: `${AlignmentTypes.bottom},${AlignmentTypes.right}`, exportOriginHorizontal: AlignmentTypes.right, exportOriginVertical: AlignmentTypes.bottom },
        { name: 'Top Left', value: `${AlignmentTypes.top},${AlignmentTypes.left}`, exportOriginHorizontal: AlignmentTypes.left, exportOriginVertical: AlignmentTypes.top },
        { name: 'Top Right', value: `${AlignmentTypes.top},${AlignmentTypes.right}`, exportOriginHorizontal: AlignmentTypes.right, exportOriginVertical: AlignmentTypes.top }
      ]
    },
    optimiserExportOrigin() {
      return `${this.optimiserConfig.exportOriginVertical},${this.optimiserConfig.exportOriginHorizontal}`
    },
    optimiserFillVertOptions() {
      return OptimiserFillDirectionTypeLookup.filter(type => type.value == OptimiserFillDirectionTypes.up || type.value == OptimiserFillDirectionTypes.down)
    },
    optimiserFillHorzOptions() {
      return OptimiserFillDirectionTypeLookup.filter(type => type.value == OptimiserFillDirectionTypes.left || type.value == OptimiserFillDirectionTypes.right)
    },
  },
  methods: {
    show(productionStation) {
      // Reset form
      this.stationData = { }
      this.stationForm = {}
      this.submitting = false

      if(productionStation != null) {
        this.stationData = _.cloneDeep(productionStation)
      }

      let wjConfig = this.stationData.waterjetBedConfig || {}
      let optiConfig = this.stationData.optimiserConfig || {}
      let furnConfig = this.stationData.furnaceConfig || {}

      this.bedConfig.positionList = wjConfig.positionList || []
      this.bedConfig.width = wjConfig.width || 0
      this.bedConfig.height = wjConfig.height || 0
      this.bedConfig.homePositionX = wjConfig.homePositionX || 0
      this.bedConfig.homePositionY = wjConfig.homePositionY || 0
      this.bedConfig.originHorizontal = wjConfig.originHorizontal || AlignmentTypes.left
      this.bedConfig.originVertical = wjConfig.originVertical || AlignmentTypes.bottom
      this.bedConfig.originGuideHeight = wjConfig.originGuideHeight || 0
      this.bedConfig.originGuideWidth = wjConfig.originGuideWidth || 0
      this.bedConfig.maxNegativeHeight = wjConfig.maxNegativeHeight || 0
      this.bedConfig.maxNegativeWidth = wjConfig.maxNegativeWidth || 0

      this.optimiserConfig.exportOriginHorizontal = optiConfig.exportOriginHorizontal || AlignmentTypes.left
      this.optimiserConfig.exportOriginVertical = optiConfig.exportOriginVertical || AlignmentTypes.bottom
      this.optimiserConfig.fillHorzDirection = optiConfig.fillHorzDirection || OptimiserFillDirectionTypes.right
      this.optimiserConfig.fillVertDirection = optiConfig.fillVertDirection || OptimiserFillDirectionTypes.up
      this.optimiserConfig.maxCuttingDepth = parseInt(optiConfig.maxCuttingDepth || 9)
      this.optimiserConfig.offcutScoringMethod = optiConfig.offcutScoringMethod || OffcutScoreConfigTypes.greaterOffcutArea
      this.optimiserConfig.offcutScoringPercent = parseFloat(optiConfig.offcutScoringPercent)
      this.optimiserConfig.offcutOverflowEnabled = !!optiConfig.offcutOverflowEnabled

      this.furnaceConfig.bedHeight = furnConfig.bedHeight || 0
      this.furnaceConfig.bedWidth = furnConfig.bedWidth || 0

      if(isNaN(this.optimiserConfig.offcutScoringPercent))
        this.optimiserConfig.offcutScoringPercent = 50
      if(isNaN(this.optimiserConfig.maxCuttingDepth))
        this.optimiserConfig.maxCuttingDepth = 9

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = {
        hardwareType: this.stationData.hardwareType,
        maxFeedHeight: this.stationData.maxFeedHeight,
        waterjetBedConfig: {
          positionList: (this.bedConfig.positionList || []).map(value => parseInt(value || 0)),
          width: this.bedConfig.width,
          height: this.bedConfig.height,
          homePositionX: this.bedConfig.homePositionX,
          homePositionY: this.bedConfig.homePositionY,
          originHorizontal: this.bedConfig.originHorizontal,
          originVertical: this.bedConfig.originVertical,
          originGuideHeight: this.bedConfig.originGuideHeight,
          originGuideWidth: this.bedConfig.originGuideWidth,
          maxNegativeHeight: this.bedConfig.maxNegativeHeight,
          maxNegativeWidth: this.bedConfig.maxNegativeWidth
        },
        optimiserConfig: {
          exportOriginHorizontal: this.optimiserConfig.exportOriginHorizontal,
          exportOriginVertical: this.optimiserConfig.exportOriginVertical,
          fillHorzDirection: this.optimiserConfig.fillHorzDirection,
          fillVertDirection: this.optimiserConfig.fillVertDirection,
          maxCuttingDepth: this.optimiserConfig.maxCuttingDepth,
          offcutScoringMethod: this.optimiserConfig.offcutScoringMethod,
          offcutScoringPercent: this.optimiserConfig.offcutScoringPercent,
          offcutOverflowEnabled: this.optimiserConfig.offcutOverflowEnabled
        },
        furnaceConfig: {
          bedHeight: this.furnaceConfig.bedHeight,
          bedWidth: this.furnaceConfig.bedWidth
        }
      }

      this.$apiService.productionService.updateProductionStationHardware(this.stationData.id, updateData)
      .then(response => {
        this.submitting = false
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.submitting = false
        this.$data.stationForm = this.processFormResponse(err)
      })
    },
    updateWaterjetPosition(index, value) {
      this.bedConfig.positionList[index] = value
    },
    addWaterjetPosition() {
      this.bedConfig.positionList.push(0)
    },
    removeWaterjetPosition(index) {
      this.bedConfig.positionList.splice(index, 1)
    },
    updateWaterjetOrigin(value) {
      let lookupValue = this.waterjetOriginOptions.find(option => option.value == value)
      if(lookupValue) {
        this.bedConfig.originHorizontal = lookupValue.originHorizontal
        this.bedConfig.originVertical = lookupValue.originVertical
      }
    },
    updateOptimiserExportOrigin(value) {
      let lookupValue = this.optimiserExportOriginOptions.find(option => option.value == value)
      if(lookupValue) {
        this.optimiserConfig.exportOriginHorizontal = lookupValue.exportOriginHorizontal
        this.optimiserConfig.exportOriginVertical = lookupValue.exportOriginVertical
      }
    }
  }
}
</script>